import React, { useMemo } from 'react'
import BootstrapTable from 'react-bootstrap-table-next'
import paginationFactory from 'react-bootstrap-table2-paginator'
import filterFactory from 'react-bootstrap-table2-filter'
import { Alert, Spinner } from 'react-bootstrap'
import { useLocalInventoriesState } from '../../stores/LocalInventoriesProvider'
import UniversalTableWrapperTest from '../Table/UniversalTableWrapperTest'
import TableProvider, { useTableState } from '../Table/TableProvider'
import useMountEffect from '../../utils/hooks/useMountEffect'
import { t } from 'i18next'
import { columns } from './filterConfig.js'
import OverflowTooltip from '../../components/OverflowTooltip.jsx'

const defaultSorted = [
  {
    dataField: 'articleNumber',
    order: 'desc',
  },
]

const LocalInventoriesTableInner = ({
  noActions,
  onRowClick,
  withCheckbox,
  tableId,
}) => {
  const {
    localInventories,
    setPageOffset,
    setPageLimit,
    filter,
    setFilter,
    selectedRow,
    setShowAllLocalInventories,
    setSorting,
    tableIsLoading,
    totalCount,
    selectAll,
    allCheckBoxesSelected,
    allSelectedLoading,
    setInitialTableId,
    initialTableId,
    setSelectedCheckBoxes,
  } = useLocalInventoriesState()

  const { tableRef, paginationOptions, currentPage, totalPages } =
    useTableState()
  useMountEffect(() => {
    if (tableId !== initialTableId) {
      setInitialTableId(tableId)
    }
    setSelectedCheckBoxes([])
  })

  const handleTableChange = (type, newState) => {
    setPageLimit(newState.sizePerPage)
    if (type === 'filter') {
      setShowAllLocalInventories(false)
      const { filters } = newState
      setFilter((prev) => ({
        ...prev,
        'filter.articleNumber': filters.articleNumber
          ? filters.articleNumber.filterVal
          : '',
        'filter.articleName': filters.articleName
          ? filters.articleName.filterVal
          : '',
        'filter.sizeName': filters.sizeName ? filters.sizeName.filterVal : '',
        'filter.serialkey': filters.serialkey
          ? filters.serialkey.filterVal
          : '',
        'filter.batchKey': filters.batchKey ? filters.batchKey.filterVal : '',
        'filter.storehouseCoordinatesOrName': filters.storehouseCoordinates
          ? filters.storehouseCoordinates.filterVal
          : '',
        'filter.storehouseName': filters.storehouseName
          ? filters.storehouseName.filterVal
          : '',
      }))
    }

    if (type === 'sort') {
      const { sortOrder, sortField } = newState
      setSorting(`${sortOrder === 'asc' ? '-' : ''}${sortField}`)
    }
    setPageOffset(
      newState.page === 1
        ? 0
        : newState.sizePerPage * newState.page - newState.sizePerPage
    )
  }

  const preparedColumns = useMemo(() => {
    const tmpColumns = [...columns]
    const filteredColumns = tmpColumns.filter(
      (tmpColumn) => tmpColumn.dataField !== 'storehouseName'
    )
    filteredColumns.forEach((tmpColumn) => {
      tmpColumn.headerFormatter = (column, colIndex, components) => {
        switch (tmpColumn.dataField) {
          case 'checkBox':
            return (
              <span>
                {allSelectedLoading ? (
                  <Spinner animation='border' variant='primary' size='sm' />
                ) : (
                  <input
                    type='checkbox'
                    id='checkbox_header'
                    name='scales'
                    checked={allCheckBoxesSelected}
                    onChange={selectAll}
                  ></input>
                )}
              </span>
            )
          default:
            return (
              <>
                <OverflowTooltip text={column.text}>
                  {column.text}
                  {components.sortElement}
                  <div className='d-flex align-items-end'>
                    {components.filterElement}
                  </div>
                </OverflowTooltip>
              </>
            )
        }
      }
    })
    tmpColumns.forEach((tmpColumn) => {
      tmpColumn.text = t(tmpColumn.text)
    })
    const ignoreColumns = () => {
      var result = []
      if (noActions) result.push('actions')
      if (!withCheckbox) result.push('checkBox')

      return result
    }
    return filteredColumns.filter(
      (item) => !ignoreColumns().includes(item.dataField)
    )
  }, [
    allSelectedLoading,
    allCheckBoxesSelected,
    selectAll,
    noActions,
    withCheckbox,
  ])

  const getChosenFilters = () => {
    var message = []
    //save filters in Array
    if (filter) {
      message = Object.keys(filter).filter((key) => filter[key] !== '')
    }
    return message
  }
  const noDataIndicationMessage = () => {
    const chosenFilters = getChosenFilters()
    const alertMessage = t('localInventoriesTable:alertMsg')
    //returns Alert if no results for used filters
    if (totalCount === 0 && chosenFilters.length > 0 && !tableIsLoading) {
      return (
        <div className='no-data-placeholder-alert'>
          <Alert variant='info'>{alertMessage}</Alert>
        </div>
      )
    }
    //returns empty Table
    else {
      return (
        <div className='no-data-placeholder'>
          {t('localInventoriesTable:pleaseSearchLocalInventory')}
        </div>
      )
    }
  }
  return (
    <UniversalTableWrapperTest
      tableIsLoading={tableIsLoading}
      currentPage={currentPage}
      totalPages={totalPages}
    >
      <BootstrapTable
        ref={tableRef}
        remote
        loading={tableIsLoading}
        noDataIndication={() => noDataIndicationMessage()}
        onTableChange={handleTableChange}
        bootstrap4
        keyField='id'
        hover
        data={localInventories}
        columns={preparedColumns}
        defaultSorted={defaultSorted}
        rowEvents={{ onClick: onRowClick }}
        rowClasses={(row, rowIndex) => {
          let classes = []
          if (row === selectedRow) {
            classes.push('active')
          }
          if (row.isDeleted) {
            classes.push('is-deleted')
          }
          return classes.join(' ')
        }}
        pagination={paginationFactory(paginationOptions)}
        filter={filterFactory()}
        bordered={false}
      />
    </UniversalTableWrapperTest>
  )
}

const LocalInventoriesTable = ({
  noActions,
  onRowClick,
  withCheckbox,
  tableId,
}) => {
  const {
    pageOffset,
    setPageOffset,
    pageLimit,
    setPageLimit,
    totalCount,
    setTotalCount,
  } = useLocalInventoriesState()

  const providerSettings = {
    pageOffset,
    setPageOffset,
    pageLimit,
    setPageLimit,
    totalCount,
    setTotalCount,
  }
  return (
    <TableProvider {...providerSettings}>
      <LocalInventoriesTableInner
        noActions={noActions}
        withCheckbox={withCheckbox}
        onRowClick={onRowClick}
        tableId={tableId}
      />
    </TableProvider>
  )
}

export default LocalInventoriesTable
